/**
 * Pose detection using MediaPipe — detects body landmarks to overlay
 * measurement lines on a try-on result image.
 */

export interface MeasurementLine {
  y: number;
  x1: number;
  x2: number;
}

export interface MeasurementLines {
  chest: MeasurementLine;
  waist: MeasurementLine;
  hips: MeasurementLine;
}

/** Extended landmarks for per-field measurement guides.
 *  Each point now carries:
 *    - x, y: normalized image coords (0–1)
 *    - z: depth-into-photo (BlazePose's hip-centered z, in normalized units)
 *    - visibility: 0–1 confidence the joint was actually seen
 *  z + eyes + heels enable: pelvic tilt detection, IPD sanity check,
 *  femur-based scaling (per Gemini's recommendations).
 */
export interface JointPoint {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}
export interface BodyLandmarks {
  leftShoulder: JointPoint;
  rightShoulder: JointPoint;
  leftHip: JointPoint;
  rightHip: JointPoint;
  leftElbow: JointPoint;
  rightElbow: JointPoint;
  leftWrist: JointPoint;
  rightWrist: JointPoint;
  leftKnee: JointPoint;
  rightKnee: JointPoint;
  leftAnkle: JointPoint;
  rightAnkle: JointPoint;
  nose: JointPoint;
  // Optional (only present after upgrade to World Landmarks + extended joints)
}

/** Pixel dimensions of the image the landmarks were detected on. Sent to
 *  the backend alongside the landmarks so it can convert normalized x/y
 *  into pixels — without this, horizontal and vertical distances on a
 *  non-square image are skewed by the aspect ratio. */
export interface BodyLandmarksWithDims extends BodyLandmarks {
  imageWidth: number;
  imageHeight: number;
}

const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_ELBOW = 13;
const RIGHT_ELBOW = 14;
const LEFT_WRIST = 15;
const RIGHT_WRIST = 16;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;
const LEFT_KNEE = 25;
const RIGHT_KNEE = 26;
const LEFT_ANKLE = 27;
const RIGHT_ANKLE = 28;
const NOSE = 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let poseLandmarker: any = null;
let loadingPromise: Promise<void> | null = null;

async function loadMediaPipe(): Promise<void> {
  if (poseLandmarker) return;
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    try {
      const vision = await import(
        /* webpackIgnore: true */
        // @ts-ignore dynamic CDN import
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/vision_bundle.mjs"
      );
      const { FilesetResolver, PoseLandmarker } = vision;
      const filesetResolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/wasm"
      );
      const createPoseLandmarker = async (delegate: "GPU" | "CPU") => PoseLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
          delegate,
        },
        runningMode: "IMAGE",
        numPoses: 1,
      });
      try {
        poseLandmarker = await createPoseLandmarker("GPU");
      } catch (gpuErr) {
        console.warn("[PS-SDK:MediaPipe] GPU delegate failed, retrying with CPU delegate:", gpuErr);
        poseLandmarker = await createPoseLandmarker("CPU");
      }
    } catch (err) {
      loadingPromise = null;
      throw err;
    }
  })();
  return loadingPromise;
}

export async function preloadBodyLandmarker(): Promise<void> {
  await loadMediaPipe();
}

export async function detectMeasurementLines(
  imageSrc: string
): Promise<MeasurementLines | null> {
  try {
    await loadMediaPipe();
    const img = await loadImage(imageSrc);
    const result = poseLandmarker.detect(img);
    if (!result?.landmarks?.length || result.landmarks[0].length < 25) {
      return null;
    }
    const landmarks = result.landmarks[0];
    const lShoulder = landmarks[LEFT_SHOULDER];
    const rShoulder = landmarks[RIGHT_SHOULDER];
    const lHip = landmarks[LEFT_HIP];
    const rHip = landmarks[RIGHT_HIP];
    const shoulderY = (lShoulder.y + rShoulder.y) / 2;
    const hipY = (lHip.y + rHip.y) / 2;
    const torsoLen = hipY - shoulderY;
    const chestY = shoulderY + torsoLen * 0.28;
    const shoulderWidth = Math.abs(rShoulder.x - lShoulder.x);
    const chestX1 = Math.min(lShoulder.x, rShoulder.x) - shoulderWidth * 0.05;
    const chestX2 = Math.max(lShoulder.x, rShoulder.x) + shoulderWidth * 0.05;
    const hipWidth = Math.abs(rHip.x - lHip.x);
    const hipX1 = Math.min(lHip.x, rHip.x) - hipWidth * 0.15;
    const hipX2 = Math.max(lHip.x, rHip.x) + hipWidth * 0.15;
    const waistY = chestY + (hipY - chestY) * 0.55;
    const waistWidth = shoulderWidth * 0.85;
    const waistCenterX =
      (lShoulder.x + rShoulder.x + lHip.x + rHip.x) / 4;
    const waistX1 = waistCenterX - waistWidth / 2;
    const waistX2 = waistCenterX + waistWidth / 2;
    return {
      chest: { y: chestY, x1: chestX1, x2: chestX2 },
      waist: { y: waistY, x1: waistX1, x2: waistX2 },
      hips: { y: hipY, x1: hipX1, x2: hipX2 },
    };
  } catch (err) {
    console.error("[PS-SDK] Pose detection failed:", err);
    return null;
  }
}

/** Load an image, trying CORS first then falling back to no-CORS */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => {
      // Retry without CORS — image will be tainted but MediaPipe can still detect
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = () => reject(new Error("Failed to load image"));
      img2.src = src;
    };
    img.src = src;
  });
}

/** Downsample an image to a maximum long-edge size. MediaPipe's pose
 *  landmarker is trained at 256×256 internally — a 1024px input gives
 *  pixel-perfect detection at a fraction of the time/memory of full-res
 *  3024×4032 photos. The segmentation mask scales linearly: a 1024px input
 *  produces a ~30 KB mask PNG instead of 258 KB.
 *  Returns the original image element if it's already small enough. */
async function downsampleIfNeeded(img: HTMLImageElement, maxLongEdge: number = 1024): Promise<HTMLImageElement | HTMLCanvasElement> {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const longEdge = Math.max(w, h);
  if (longEdge <= maxLongEdge) return img;
  const scale = maxLongEdge / longEdge;
  const newW = Math.round(w * scale);
  const newH = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return img;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, newW, newH);
  console.log(`[PS-SDK:Downsample] ${w}×${h} → ${newW}×${newH} (max long edge ${maxLongEdge}px)`);
  return canvas;
}

/** Detect full body landmarks for per-field measurement guides.
 *  Accepts a URL string OR an already-loaded HTMLImageElement to avoid CORS issues. */
export async function detectBodyLandmarks(
  imageSrc: string | HTMLImageElement,
  options: { maxLongEdge?: number } = {}
): Promise<BodyLandmarksWithDims | null> {
  try {
    await loadMediaPipe();
    let img: HTMLImageElement;
    if (typeof imageSrc === "string") {
      img = await loadImage(imageSrc);
    } else {
      img = imageSrc;
    }
    // Downsample huge phone photos before running MediaPipe — keeps
    // landmark accuracy while shrinking mask size + detect time by ~10×.
    // We report the WORKING canvas dims (post-downsample) as
    // `imageWidth/Height` so backend + Gemini see the same scale as the
    // bytes they receive. Reporting the pre-downsample original here is
    // a lie — Gemini's pixel-scale math then computes positions that
    // don't exist in the actual image bytes.
    const inputForDetect: HTMLImageElement | HTMLCanvasElement = await downsampleIfNeeded(img, options.maxLongEdge ?? 1024);
    const workingW = (inputForDetect as HTMLImageElement | HTMLCanvasElement).width || img.naturalWidth || img.width;
    const workingH = (inputForDetect as HTMLImageElement | HTMLCanvasElement).height || img.naturalHeight || img.height;
    const result = poseLandmarker.detect(inputForDetect as any);
    if (!result?.landmarks?.length || result.landmarks[0].length < 29) {
      console.warn("[PS-SDK:MediaPipe] detection returned no usable landmarks", {
        gotLandmarks: !!result?.landmarks?.length,
        jointCount: result?.landmarks?.[0]?.length ?? 0,
      });
      return null;
    }
    const lm = result.landmarks[0];
    // World landmarks: same 33 points but in real-meter space, hip-centered.
    // Provides reliable z-coordinate even when the image landmarks' z is
    // in a different scale.
    const wlm = result.worldLandmarks?.[0] ?? null;
    const pickPoint = (idx: number, useWorld: boolean = false): JointPoint => {
      const src = useWorld && wlm ? wlm[idx] : lm[idx];
      const out: JointPoint = { x: src.x, y: src.y };
      if (typeof src.z === "number") out.z = src.z;
      if (typeof src.visibility === "number") out.visibility = src.visibility;
      return out;
    };
    const out: BodyLandmarksWithDims = {
      leftShoulder: pickPoint(LEFT_SHOULDER),
      rightShoulder: pickPoint(RIGHT_SHOULDER),
      leftHip: pickPoint(LEFT_HIP),
      rightHip: pickPoint(RIGHT_HIP),
      leftElbow: pickPoint(LEFT_ELBOW),
      rightElbow: pickPoint(RIGHT_ELBOW),
      leftWrist: pickPoint(LEFT_WRIST),
      rightWrist: pickPoint(RIGHT_WRIST),
      leftKnee: pickPoint(LEFT_KNEE),
      rightKnee: pickPoint(RIGHT_KNEE),
      leftAnkle: pickPoint(LEFT_ANKLE),
      rightAnkle: pickPoint(RIGHT_ANKLE),
      nose: pickPoint(NOSE),
      imageWidth: workingW,
      imageHeight: workingH,
    };
    // Verbose pipeline log — groups together so it's easy to expand in
    // DevTools. Shows the raw normalized landmarks + z-coord + visibility
    // + image dimensions so you can cross-reference what the backend will see.
    const isJointPoint = (v: unknown): v is JointPoint =>
      !!v && typeof v === "object" && typeof (v as any).x === "number" && typeof (v as any).y === "number";
    const jointCount = Object.values(out).filter(isJointPoint).length;
    const hasZ = lm.some((p: any) => typeof p.z === "number");
    const hasWorld = !!wlm;
    console.groupCollapsed(`[PS-SDK:MediaPipe] ✓ ${jointCount} joints detected @ ${out.imageWidth}×${out.imageHeight}px (z=${hasZ ? "yes" : "no"}, worldLandmarks=${hasWorld ? "yes" : "no"})`);
    for (const [name, v] of Object.entries(out)) {
      if (!isJointPoint(v)) continue;
      const p = v;
      const z = typeof p.z === "number" ? `, z=${p.z.toFixed(3)}` : "";
      const vis = typeof p.visibility === "number" ? `, vis=${p.visibility.toFixed(2)}` : "";
      console.log(`  ${name.padEnd(15)} (${p.x.toFixed(3)}, ${p.y.toFixed(3)}${z}${vis})`);
    }
    console.log(`  imageWidth      ${out.imageWidth}`);
    console.log(`  imageHeight     ${out.imageHeight}`);
    console.groupEnd();
    return out;
  } catch (err) {
    console.error("[PS-SDK:MediaPipe] Body landmark detection failed:", err);
    return null;
  }
}
